// src/extenders/find-in-store-modal.js
var FindInStoreModal = class extends HTMLElement {
  constructor() {
    super();
    this.selectedSize = null;
    this.variantObserver = null;
    this.boundVariantChangeHandler = null;
    this.boundVariantInputHandler = null;
    this.boundSizeChangeHandler = null;
  }
  /**
   * Setup listener for variant changes on the product form
   */
  setupVariantListener() {
    if (!this.productForm)
      return;
    this.boundVariantChangeHandler = (event) => {
      var _a;
      this.updateModalContent((_a = event.detail) == null ? void 0 : _a.variantId);
    };
    document.addEventListener("variant-change", this.boundVariantChangeHandler);
    const variantInput = this.productForm.querySelector('input[name="productVariant"]');
    if (variantInput) {
      this.variantObserver = new MutationObserver(() => {
        this.updateModalContent(variantInput.value);
      });
      this.variantObserver.observe(variantInput, {
        attributes: true,
        attributeFilter: ["value"]
      });
      this.boundVariantInputHandler = () => {
        this.updateModalContent(variantInput.value);
      };
      variantInput.addEventListener("change", this.boundVariantInputHandler);
      this.variantInput = variantInput;
    }
  }
  connectedCallback() {
    var _a;
    this.productForm = document.querySelector("product-form");
    this.variantDataElement = document.querySelector(".variants-data");
    this.storeStatusElement = this.querySelector(".js-store-status");
    this.selectedVariantElement = this.querySelector(".js-selected-variant");
    this.sizeSelectorElement = this.querySelector(".js-size-selector");
    this.sizeSelectElement = (_a = this.sizeSelectorElement) == null ? void 0 : _a.querySelector(".size-selector__select");
    this.variantsData = this.variantDataElement ? JSON.parse(this.variantDataElement.innerHTML) : null;
    this.lowStockThresholdStore = this.variantDataElement ? parseInt(this.variantDataElement.dataset.lowStockThresholdStore) || 5 : 5;
    this.setupVariantListener();
    this.updateModalContent();
    this.populateSizeSelector();
    this.setupModalHandlers();
  }
  /**
   * Cleanup when element is removed from DOM
   */
  disconnectedCallback() {
    if (this.boundVariantChangeHandler) {
      document.removeEventListener("variant-change", this.boundVariantChangeHandler);
    }
    if (this.variantObserver) {
      this.variantObserver.disconnect();
    }
    if (this.variantInput && this.boundVariantInputHandler) {
      this.variantInput.removeEventListener("change", this.boundVariantInputHandler);
    }
    if (this.sizeSelectElement && this.boundSizeChangeHandler) {
      this.sizeSelectElement.removeEventListener("change", this.boundSizeChangeHandler);
    }
    if (this.modalButtons) {
      this.modalButtons.forEach((button) => {
        if (this.boundModalClickHandler) {
          button.removeEventListener("click", this.boundModalClickHandler);
        }
      });
    }
  }
  /**
   * Update modal content with current variant information
   * @param {string} variantId - Optional variant ID, if not provided will get from form
   */
  updateModalContent(variantId = null) {
    if (!variantId && this.productForm) {
      const variantInput = this.productForm.querySelector('input[name="productVariant"]');
      variantId = variantInput == null ? void 0 : variantInput.value;
    }
    if (!variantId || !this.variantsData)
      return;
    const variant = this.variantsData.variants.find((v) => v.id == variantId);
    if (!variant)
      return;
    this.updateVariantDisplay(variant);
    this.updateStoreAvailability(variant);
  }
  /**
   * Update the variant display text (e.g., "Size: M / Color: Black")
   * @param {Object} variant - Variant data object
   */
  updateVariantDisplay(variant) {
    if (!this.selectedVariantElement)
      return;
    const variantTitle = variant.title || "";
    if (variantTitle && variantTitle !== "Default Title") {
      this.selectedVariantElement.textContent = variantTitle;
    } else {
      this.selectedVariantElement.textContent = "";
    }
  }
  /**
   * Update store availability status
   * @param {Object} variant - Variant data object
   */
  updateStoreAvailability(variant) {
    if (!this.storeStatusElement)
      return;
    const storeInventory = variant.inventory_in_store || 0;
    this.storeStatusElement.innerHTML = "";
    const statusSpan = document.createElement("span");
    statusSpan.className = "stock-status";
    if (storeInventory <= 0) {
      statusSpan.classList.add("sold-out");
      statusSpan.innerHTML = `<span class="stock-status__dot stock-status__dot--red"></span>${this.getTranslation("sold_out")}`;
    } else if (storeInventory <= this.lowStockThresholdStore) {
      statusSpan.classList.add("low-stock");
      statusSpan.innerHTML = `<span class="stock-status__dot stock-status__dot--orange"></span>${this.getTranslation("low_in_stock")}`;
    } else {
      statusSpan.classList.add("in-stock");
      statusSpan.innerHTML = `<span class="stock-status__dot stock-status__dot--green"></span>${this.getTranslation("in_stock")}`;
    }
    this.storeStatusElement.appendChild(statusSpan);
  }
  /**
   * Populate size selector with all available sizes
   */
  populateSizeSelector() {
    if (!this.sizeSelectElement || !this.variantsData)
      return;
    const sizes = /* @__PURE__ */ new Set();
    const sizeAvailability = {};
    this.variantsData.variants.forEach((variant) => {
      const options = variant.title.split(" / ");
      const size = options[options.length - 1];
      if (size && size !== "Default Title") {
        sizes.add(size);
        if (!sizeAvailability[size]) {
          sizeAvailability[size] = {
            storeInventory: 0,
            hasAnyInventory: false
          };
        }
        sizeAvailability[size].storeInventory = Math.max(
          sizeAvailability[size].storeInventory,
          variant.inventory_in_store || 0
        );
        if (variant.inventory_in_store > 0) {
          sizeAvailability[size].hasAnyInventory = true;
        }
      }
    });
    const firstOption = this.sizeSelectElement.querySelector("option");
    this.sizeSelectElement.innerHTML = "";
    if (firstOption) {
      this.sizeSelectElement.appendChild(firstOption);
    }
    sizes.forEach((size) => {
      const option = document.createElement("option");
      option.value = size;
      option.textContent = size;
      this.sizeSelectElement.appendChild(option);
    });
    this.boundSizeChangeHandler = (e) => {
      this.handleSizeSelection(e.target.value);
    };
    this.sizeSelectElement.addEventListener("change", this.boundSizeChangeHandler);
  }
  /**
   * Handle size selection
   * @param {string} size - Selected size
   */
  handleSizeSelection(size) {
    if (!size)
      return;
    this.selectedSize = size;
    const variant = this.variantsData.variants.find((v) => {
      const options = v.title.split(" / ");
      const variantSize = options[options.length - 1];
      return variantSize === size;
    });
    if (variant) {
      this.updateStoreAvailability(variant);
      this.selectedVariantElement.textContent = size;
    }
  }
  /**
   * Get translation from window.product.translations
   * @param {string} key - Translation key
   * @returns {string} Translated text
   */
  getTranslation(key) {
    var _a, _b;
    if ((_b = (_a = window.product) == null ? void 0 : _a.translations) == null ? void 0 : _b[key]) {
      return window.product.translations[key];
    }
    return key;
  }
  /**
   * Setup modal open/close handlers
   */
  setupModalHandlers() {
    this.modalButtons = document.querySelectorAll('.js-handle-modal[data-target-id="find-in-store-modal"]');
    this.boundModalClickHandler = () => {
      this.updateModalContent();
    };
    this.modalButtons.forEach((button) => {
      button.addEventListener("click", this.boundModalClickHandler);
    });
  }
};
if (customElements.get("find-in-store-modal") === void 0) {
  customElements.define("find-in-store-modal", FindInStoreModal);
}
